import type {FieldTypeId} from 'src/typings';
import type {GridField, RecordRow} from 'src/features/record-grid/lib/types';
import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import {getTextFromGridFieldValue, isGridFieldValueEmpty} from '../../sutor-utils';
import {exists} from 'src/shared/utils/custom-utils';
import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';

interface ValueMapping {
    fieldId: FieldTypeId;
    label: string;
    unitFieldId?: FieldTypeId;
    customUnit?: string;
}

interface GetValueSummaryConfig {
    values: ValueMapping[];
    extraValues?: (row: RecordRow) => ValueMapping[];
}

interface ValueSummary {
    value: string;
    tooltipLabel: string;
}

export function getValueSummary(row: RecordRow, config: GetValueSummaryConfig): ValueSummary {
    const valueConfigs = [
        ...config.values,
        ...(config.extraValues ? config.extraValues(row) : [])
    ];

    const values: {
        valueField: GridField;
        unitField?: GridField;
        customUnit?: string;
        tooltipLabel: string;
    }[] = [];

    valueConfigs.forEach((mapping) => {
        const valueField = getFirstFieldByFieldTypeIdRecursive(row, mapping.fieldId);

        if (isGridFieldValueEmpty(valueField)) {
            return;
        }

        const unitField = mapping.unitFieldId
            ? getFirstFieldByFieldTypeIdRecursive(row, mapping.unitFieldId)
            : undefined;

        values.push({
            valueField,
            unitField,
            customUnit: mapping.customUnit,
            tooltipLabel: mapping.label
        });
    });

    return {
        value: values
            .map((v) => {
                if (exists(v.customUnit)) {
                    return `${getTextFromGridFieldValue(v.valueField)} ${v.customUnit}`;
                }

                if (!exists(v.unitField)) {
                    return getTextFromGridFieldValue(v.valueField);
                }

                return `${getTextFromGridFieldValue(v.valueField)} ${getTextFromGridFieldValue(v.unitField)}`;
            })
            .join(' | '),
        tooltipLabel: values.map((v) => v.tooltipLabel).join(' | ')
    };
}

export function getReportReportedValue(row: RecordRow, displayThingReportValues: boolean = true): ValueSummary {
    return getValueSummary(row, {
        values: [
            {
                fieldId: FOND_REPORT.reportedPersonStandardFieldId,
                label: 'Vykázáno pracovník',
                unitFieldId: FOND_REPORT.personUnitFieldId
            },
            {
                fieldId: FOND_REPORT.reportedPersonOvertimeFieldId,
                label: 'Vykázáno přesčas pracovník',
                unitFieldId: FOND_REPORT.personUnitFieldId
            },
            {
                fieldId: FOND_REPORT.reportedMachineFieldId,
                label: 'Vykázáno stroj',
                unitFieldId: FOND_REPORT.machineUnitFieldId
            }
        ],
        extraValues: (recRow) =>
            FOND_REPORT.thingReports.includes(recRow.fond.id) && displayThingReportValues
                ? [
                    {
                        fieldId: FOND_REPORT.sellCountFieldId,
                        label: 'Množství',
                        unitFieldId: FOND_REPORT.sellUnitFieldId
                    },
                    {
                        fieldId: FOND_REPORT.sellPriceFieldId,
                        label: 'Cena za jednotku',
                        customUnit: 'Kč'
                    }
                ]
                : []
    });
}

export function getReportChargedValue(row: RecordRow): ValueSummary {
    return getValueSummary(row, {
        values: [
            {
                fieldId: FOND_REPORT.chargedPersonStandardFieldId,
                label: 'Účtováno pracovník',
                unitFieldId: FOND_REPORT.personUnitFieldId
            },
            {
                fieldId: FOND_REPORT.chargedPersonOvertimeFieldId,
                label: 'Účtováno přesčas pracovník',
                unitFieldId: FOND_REPORT.personUnitFieldId
            },
            {
                fieldId: FOND_REPORT.chargedMachineFieldId,
                label: 'Účtováno stroj',
                unitFieldId: FOND_REPORT.machineUnitFieldId
            },
            {
                fieldId: FOND_REPORT.chargedMachineOvertimeFieldId,
                label: 'Účtováno přesčas stroj',
                customUnit: 'hod'
            }
        ],
        extraValues: (recRow) =>
            FOND_REPORT.thingReports.includes(recRow.fond.id)
                ? [
                    {
                        fieldId: FOND_REPORT.sellCountFieldId,
                        label: 'Množství',
                        unitFieldId: FOND_REPORT.sellUnitFieldId
                    },
                    {
                        fieldId: FOND_REPORT.sellPriceFieldId,
                        label: 'Cena za jednotku',
                        customUnit: 'Kč'
                    }
                ]
                : []
    });
}
